import { useGesture, UserGestureConfig } from "@use-gesture/react";
import { Lethargy } from "lethargy-ts";
import { RefObject, useEffect, useMemo, useRef } from "react";
import debounce from "lodash.debounce";
import { useSlideKeyboardNavigation } from "./useSlideKeybordNavigation";
import { useInView } from "motion/react";

type UseHeroScrollerArguments = {
  onScrollDown?: () => void;
  target: UserGestureConfig["target"];
  enabled?: boolean;
};

const lethargy = new Lethargy();

export const useHeroScroller = ({
  target,
  onScrollDown,
  enabled = true,
}: UseHeroScrollerArguments) => {
  const onScrollDownRef = useRef(onScrollDown);
  const isInView = useInView(target as RefObject<HTMLElement>, { amount: 0.9 });

  useEffect(() => {
    onScrollDownRef.current = onScrollDown;
  }, [onScrollDown]);

  const onSwipeDebounced = useMemo(
    () =>
      debounce(
        () => {
          onScrollDownRef.current?.();
        },
        200,
        { leading: true, trailing: false },
      ),
    [],
  );

  useSlideKeyboardNavigation({
    enabled: isInView,
    onArrowDown: () => {
      onSwipeDebounced();
    },
  });

  useGesture(
    {
      onWheel: ({ event, direction: [, directionY] }) => {
        const isIntended = lethargy.check(event);

        if (!isIntended) return;

        const isScrollingDown = directionY > 0;
        if (isScrollingDown) {
          event.preventDefault();
          event.stopPropagation();

          onSwipeDebounced();
        }
      },

      onDrag: ({
        swipe: [, swipeY],
        velocity: [, velocityY],
        direction: [, rawDirectionY],
      }) => {
        const hasMouse = window.matchMedia("(pointer: fine)").matches;
        const isTouch = window.matchMedia("(pointer: coarse)").matches;

        if ((hasMouse && !isTouch) || !isInView) {
          return;
        }

        const isSwipeUp =
          swipeY < -5 || (rawDirectionY === -1 && velocityY > 0.3);

        if (isSwipeUp) {
          onSwipeDebounced();
        }
      },
    },
    {
      target,
      enabled,
      eventOptions: {
        passive: false,
      },
      drag: {
        swipe: {
          velocity: 0.25,
        },
      },
    },
  );
};
